import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { MobileSearchOverlay } from '../MobileSearchOverlay'
import { AccountSwitcher } from '../AccountSwitcher'
import { useAuthStore } from '../../auth/useAuthStore'

function useRouteTitle(): string {
  const location = useLocation()
  const path = location.pathname
  if (path === '/') return 'Home'
  if (path.startsWith('/trades')) return 'Trades'
  if (path.startsWith('/transactions')) return 'Transactions'
  if (path.startsWith('/calendar')) return 'Calendar'
  if (path.startsWith('/watchlist')) return 'Watchlist'
  if (path.startsWith('/positions')) return 'Positions'
  if (path.startsWith('/settings')) return 'Settings'
  if (path.startsWith('/instruments/')) {
    const sym = path.split('/')[2] ?? ''
    return sym ? sym.toUpperCase() : 'Instrument'
  }
  return 'Trading Console'
}

export function MobileTopBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const navigate = useNavigate()
  const setAccountId = useAuthStore((s) => s.setAccountId)
  const [searchOpen, setSearchOpen] = useState(false)
  const title = useRouteTitle()
  const location = useLocation()
  const onTicker = location.pathname.startsWith('/instruments/')

  return (
    <>
      <header
        className="lg:hidden row-start-1 col-span-full border-b border-zinc-800 bg-zinc-950/95 backdrop-blur-md pt-safe relative z-10"
        role="banner"
      >
        <div className="flex items-center h-12 px-3 gap-2">
          {onTicker ? (
            <BackButton />
          ) : (
            <button
              type="button"
              onClick={onOpenMenu}
              aria-label="Open menu"
              className="text-zinc-400 flex items-center justify-center w-10 h-10 -ml-2 shrink-0 hover:text-zinc-200 transition"
            >
              <HamburgerIcon />
            </button>
          )}
          <h1 className="flex-1 min-w-0 text-base font-semibold text-zinc-100 truncate">
            {title}
          </h1>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Open search"
            className="w-10 h-10 flex items-center justify-center text-zinc-300 hover:text-zinc-100 transition shrink-0"
          >
            <SearchIcon />
          </button>
          <AccountSwitcher variant="header" onChange={setAccountId} />
        </div>
      </header>
      {searchOpen && (
        <MobileSearchOverlay
          onClose={() => setSearchOpen(false)}
          onSelect={(symbol) => navigate(`/instruments/${symbol}`)}
        />
      )}
    </>
  )
}

function BackButton() {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigate(-1)}
      aria-label="Back"
      className="w-10 h-10 -ml-2 flex items-center justify-center text-zinc-300 hover:text-zinc-100 transition"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-5 h-5"
        aria-hidden="true"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  )
}

function HamburgerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

